import type { PublicationContext } from '~/types';

export interface AudioPlaylistItem {
  sourceId: string;
}

/*
 * A Kilkaya stream entry as seen by the client. The proxy route
 * (server/api/kilkaya/[streamId].get.ts) returns `{ id, type }` objects, while
 * the local mock route returns bare id strings — both shapes are accepted.
 */
type AudioStreamItem = string | { id?: string } | null | undefined;

interface AudioStreamResponse {
  items?: AudioStreamItem[];
}

interface AudioPlaylistOptions {
  /* Article pinned to the front of the playlist — the one being read. */
  pinFirst?: MaybeRefOrGetter<string | undefined>;
  /* Editor-configured stream override; falls back to the publication config. */
  streamId?: MaybeRefOrGetter<string | undefined>;
  /* Gates the request, e.g. until the reader is known to have audio access. */
  enabled?: MaybeRefOrGetter<boolean>;
}

/*
 * Normalises a Kilkaya stream response into a BeyondWords playlist:
 * `pinFirst` (the article currently being read) always leads, ids are
 * deduplicated, and the result is capped at `max`.
 */
export const buildPlaylistItems = (items: AudioStreamItem[] | undefined, options: { pinFirst?: string; max: number }): AudioPlaylistItem[] => {
  const streamIds = (items || [])
    .map(item => (typeof item === 'string' ? item : item?.id))
    .filter((id): id is string => !!id);
  const pinned = options.pinFirst ? [options.pinFirst] : [];

  return [...new Set([...pinned, ...streamIds])]
    .slice(0, Math.max(options.max, 0))
    .map(sourceId => ({ sourceId }));
};

/*
 * Resolves the most-read audio stream as a BeyondWords playlist. Must be called
 * from setup — it registers a TanStack query — and yields an empty playlist
 * while loading, when the stream is unconfigured, or when the request fails.
 */
export default function useAudioPlaylist (options: AudioPlaylistOptions = {}) {
  const publicConfig = useRuntimeConfig().public as unknown as PublicationContext;
  const { replaceUrl } = useUrlReplace();
  const { query } = useTansQuery();
  const maxPlaylistItems = publicConfig.beyondWords?.maxPlaylistItems ?? 5;

  /*
   * The stream is chosen by, in order: an explicit `streamId`
   * (editor-configured), `beyondWords.streamId`, then
   * `kilkaya.options.streamId`.
   */
  const streamId = computed(() => toValue(options.streamId) || publicConfig.beyondWords?.streamId || publicConfig.kilkaya?.options?.streamId || '');
  const api = computed(() => {
    const kilkayaApi = publicConfig.kilkaya?.api;

    return streamId.value && kilkayaApi ? replaceUrl(kilkayaApi, { streamId: streamId.value, size: `${maxPlaylistItems}` }) : '';
  });
  const enabled = computed(() => !!api.value && !!publicConfig.beyondWords?.projectId && (toValue(options.enabled) ?? true));

  let streamResponse = ref<AudioStreamResponse | null>();
  let pending = ref(false);

  /*
   * Client-only by design: the playlist feeds the BeyondWords player, which
   * never renders on the server, and `useTansQuery` awaits `suspense()` inside
   * `onServerPrefetch` — a promise that never settles while a query is
   * disabled.
   */
  if (import.meta.client) {
    const result = query({ api, enabled, type: 'audio-playlist', streamId, size: maxPlaylistItems }) as {
      data: Ref<AudioStreamResponse | null | undefined>;
      loading: Ref<boolean>;
    };

    streamResponse = result.data;
    pending = result.loading;
  }

  /* A disabled query stays pending forever, so report it as settled instead. */
  const loading = computed(() => enabled.value && pending.value);
  const playlistItems = computed(() => buildPlaylistItems(streamResponse.value?.items, { pinFirst: toValue(options.pinFirst), max: maxPlaylistItems }));

  return {
    playlistItems,
    loading,
    maxPlaylistItems
  };
}
